'use client';

import { useEffect, useRef, useState } from 'react';
import { getPaymentStatus } from '@/app/actions/orders';
import type { PaymentStatusDto } from '@peach/types/payments/payment-status.dto.js';
import { callAction } from '@/lib/call-action';

/** How long the card waits for the listener to book a held call. */
const SLOT_WAIT_MS = 20_000;

/**
 * A held call is booked after the money lands, so refreshing on the money alone
 * showed a pass card saying "Pick a time": a pending slot polls on, to a ceiling.
 */
export function usePaymentPolling(sessionId: string) {
  const [status, setStatus] = useState<PaymentStatusDto | null>(null);
  const firstAnswerAt = useRef<number | null>(null);

  const settled =
    status !== null &&
    (status.slot !== 'pending' ||
      (firstAnswerAt.current !== null && Date.now() - firstAnswerAt.current >= SLOT_WAIT_MS));

  useEffect(() => {
    if (settled) return;

    let attempts = 0;
    let timeoutId: NodeJS.Timeout;

    const poll = async () => {
      const result = await callAction(() => getPaymentStatus(sessionId));
      if (result.success) {
        if (firstAnswerAt.current === null) firstAnswerAt.current = Date.now();
        setStatus(result.data);
        // The money is in, so a pending slot polls on a steady beat rather
        // than backing off; `settled` ends the effect once it flips.
        if (result.data.slot === 'pending') timeoutId = setTimeout(poll, 2000);
        return;
      }

      attempts++;
      const delay = Math.min(2000 * Math.pow(2, attempts - 1), 10000);
      timeoutId = setTimeout(poll, delay);
    };

    timeoutId = setTimeout(poll, 2000);

    return () => clearTimeout(timeoutId);
  }, [sessionId, settled]);

  // A pending status stays hidden from the caller, which refreshes on whatever
  // it is given; `bookingCall` is the only thing the card says about it.
  return { status: settled ? status : null, bookingCall: status?.slot === 'pending' };
}
